import { AssumedInjuryV2 } from 'store/ducks'
import { BodyPart, BodyParts } from './bodyPartInjuryMapping'

export interface BodyPartConfig {
  svgBaseName: string | string[]
  frontBack: boolean
  leftRight: boolean
}

export const BODY_PART_SVG_MAPPING: Record<BodyPart, BodyPartConfig> = {
  [BodyParts.HEAD]: {
    svgBaseName: 'head',
    frontBack: true,
    leftRight: false
  },
  [BodyParts.FACE]: {
    svgBaseName: 'face', // face uses face.svg (no orientation)
    frontBack: false,
    leftRight: false
  },
  [BodyParts.THROAT]: {
    svgBaseName: 'throat', // throat is anatomically front of neck - no orientation needed
    frontBack: false,
    leftRight: false
  },
  [BodyParts.NECK]: {
    svgBaseName: 'neck', // neck is anatomically back of neck - no orientation needed
    frontBack: false,
    leftRight: false
  },
  [BodyParts.COLLARBONE]: {
    svgBaseName: 'collarbone', // collarbone uses chest.svg (no variants)
    frontBack: false,
    leftRight: true
  },
  [BodyParts.SHOULDER]: {
    svgBaseName: 'shoulder',
    frontBack: true,
    leftRight: true
  },
  [BodyParts.UPPER_ARM]: {
    svgBaseName: 'upper-arm',
    frontBack: false,
    leftRight: true
  },
  [BodyParts.WRIST]: {
    svgBaseName: 'forearm-wrist',
    frontBack: false,
    leftRight: true
  },
  [BodyParts.HAND]: {
    svgBaseName: 'hand',
    frontBack: false,
    leftRight: true
  },
  [BodyParts.THUMB]: {
    svgBaseName: 'finger',
    frontBack: false, // finger files: front (finger_left/right.svg), back files prefixed with _ (ignored)
    leftRight: true
  },
  [BodyParts.ABDOMEN]: {
    svgBaseName: 'stomach',
    frontBack: false,
    leftRight: false
  },
  [BodyParts.BREAST]: {
    svgBaseName: 'chest', // breast maps to chest.svg
    frontBack: false,
    leftRight: false
  },
  [BodyParts.BACK]: {
    svgBaseName: 'back',
    frontBack: false,
    leftRight: false
  },
  [BodyParts.HIP]: {
    svgBaseName: 'hip',
    frontBack: true,
    leftRight: true
  },
  [BodyParts.PELVIS]: {
    svgBaseName: 'pelvis',
    frontBack: false,
    leftRight: false
  },
  [BodyParts.BUTTOCKS]: {
    svgBaseName: 'buttock',
    frontBack: false,
    leftRight: true
  },
  [BodyParts.GENITALIA]: {
    svgBaseName: 'genitals',
    frontBack: false,
    leftRight: false
  },
  [BodyParts.THIGH]: {
    svgBaseName: 'upper-leg',
    frontBack: true,
    leftRight: true
  },
  [BodyParts.KNEE]: {
    svgBaseName: 'knee',
    frontBack: false,
    leftRight: true
  },
  [BodyParts.LOWER_LEG]: {
    svgBaseName: 'lower-leg',
    frontBack: false,
    leftRight: true
  },
  [BodyParts.FOOT_ANKLE]: {
    svgBaseName: 'foot',
    frontBack: false,
    leftRight: true
  }
}

/**
 * Type guard to check if a string is a valid BodyPart
 */
const isBodyPart = (bodyPart: string | undefined): bodyPart is BodyPart => {
  return bodyPart !== undefined && bodyPart in BODY_PART_SVG_MAPPING
}

/**
 * Gets the SVG file path(s) for a given body part, side, and orientation
 */
export const getSvgPaths = (injury: AssumedInjuryV2) => {
  const { injuredBodyPart: bodyPart, side, orientation } = injury

  if (!bodyPart || !isBodyPart(bodyPart)) return []

  const config = BODY_PART_SVG_MAPPING[bodyPart]
  if (!config) return []

  // Use defaults if not provided
  const finalSide = side || 'right'
  const finalOrientation = orientation || 'front'

  const svgBaseNames = Array.isArray(config.svgBaseName)
    ? config.svgBaseName
    : [config.svgBaseName]

  return svgBaseNames.map((baseName: string) => {
    let fileName = baseName

    // // Handle special case for finger back orientation
    // if (baseName === 'finger' && finalOrientation === 'back') {
    //   fileName = '_finger_back'
    // }

    // Add orientation suffix if supported and provided
    if (config.frontBack) {
      // if (!(baseName === 'finger' && finalOrientation === 'back')) {
      fileName += `_${finalOrientation}`
      // }
    }

    // Add side suffix if supported and provided
    if (config.leftRight) {
      fileName += `_${finalSide}`
    }

    return `/assets/BodyParts/${fileName}.svg`
  })
}

/**
 * Checks if a body part requires side selection
 */
export const requiresSide = (bodyPart: string | undefined): boolean => {
  if (!bodyPart || !isBodyPart(bodyPart)) return false
  const config = BODY_PART_SVG_MAPPING[bodyPart]
  return config?.leftRight || false
}

/**
 * Checks if a body part requires orientation selection
 */
export const requiresOrientation = (bodyPart: string | undefined): boolean => {
  if (!bodyPart || !isBodyPart(bodyPart)) return false
  const config = BODY_PART_SVG_MAPPING[bodyPart]
  return config?.frontBack || false
}
